import { Routes, Route, Navigate } from "react-router-dom";
import { DriveAuthProvider, useDriveAuth } from "./context/DriveAuthContext";
import DriveLayout from "./components/DriveLayout";
import DriveLogin from "./pages/DriveLogin";
import DriveHub from "./pages/DriveHub";
import DriveCRM from "./pages/DriveCRM";
import DriveContracts from "./pages/DriveContracts";
import DriveDashboard from "./pages/DriveDashboard";
import DriveFiles from "./pages/DriveFiles";
import DriveArtists from "./pages/DriveArtists";
import DriveCustomers from "./pages/DriveCustomers";
import DriveActivity from "./pages/DriveActivity";
import DriveSettings from "./pages/DriveSettings";
import DriveInvoices from "./pages/DriveInvoices";
import DriveMonatsabschluss from "./pages/DriveMonatsabschluss";
import DriveMusicHub from "./pages/DriveMusicHub";
import DriveProvisions from "./pages/DriveProvisions";
import DriveArtistDetail from "./pages/DriveArtistDetail";
import DriveContactDetail from "./pages/DriveContactDetail";
import DriveFeatureDetail from "./pages/DriveFeatureDetail";
import DriveCreatorDetail from "./pages/DriveCreatorDetail";

const RequireAuth = ({ children }) => {
  const { session, loading } = useDriveAuth();
  if (loading) return <div className="min-h-screen bg-[#050505] text-zinc-500 flex items-center justify-center text-xs tracking-[0.3em] uppercase">Lade Session…</div>;
  if (!session) return <Navigate to="/drive/login" replace />;
  return children;
};

const RequireRole = ({ roles, children }) => {
  const { role } = useDriveAuth();
  if (!roles.includes(role)) return <Navigate to="/drive/hub" replace />;
  return children;
};

const DriveRoutes = () => (
  <DriveAuthProvider>
    <Routes>
      <Route path="login" element={<DriveLogin />} />
      <Route
        path="hub"
        element={
          <RequireAuth>
            <DriveLayout>
              <DriveHub />
            </DriveLayout>
          </RequireAuth>
        }
      />
      <Route
        path="dashboard"
        element={
          <RequireAuth>
            <DriveLayout>
              <DriveDashboard />
            </DriveLayout>
          </RequireAuth>
        }
      />
      <Route
        path="crm"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveCRM />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="contracts"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveContracts />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="files"
        element={
          <RequireAuth>
            <DriveLayout>
              <DriveFiles />
            </DriveLayout>
          </RequireAuth>
        }
      />
      <Route
        path="artists"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveArtists />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="customers"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveCustomers />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="invoices"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveInvoices />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="monatsabschluss"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveMonatsabschluss />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="musichub"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveMusicHub />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="provisions"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveProvisions />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="artists/:id"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveArtistDetail />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="customers/:id"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveContactDetail type="customer" />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="externals/:id"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveContactDetail type="external" />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="features/:id"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveFeatureDetail />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="creators/:id"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin", "staff"]}>
              <DriveLayout>
                <DriveCreatorDetail />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="activity"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin"]}>
              <DriveLayout>
                <DriveActivity />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route
        path="settings"
        element={
          <RequireAuth>
            <RequireRole roles={["superadmin"]}>
              <DriveLayout>
                <DriveSettings />
              </DriveLayout>
            </RequireRole>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/drive/hub" replace />} />
    </Routes>
  </DriveAuthProvider>
);

export default DriveRoutes;
